import { useMemo, useState } from 'react';
import { useCan, useShow, useTranslate } from '@refinedev/core';
import { ListButton, RefreshButton, Show } from '@refinedev/antd';
import { Button, Space, theme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { UserOut } from '@/api/generated/schemas';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { DeleteUserButton } from '@/components/iam/DeleteUserButton';
import { UserActionButtons } from '@/components/iam/UserActionButtons';
import { GrantEntitlementModal } from '@/components/monetization/GrantEntitlementModal';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import {
  USER_DEFAULT_TAB,
  USER_SHOW_TABS,
  getUserDisplayName,
} from '../constants';
import { UserShowHeader } from './UserShowHeader';
import { UserGeneralTab } from './UserGeneralTab';
import { UserConsentsTab } from './UserConsentsTab';
import { UserOAuthAccountsTab } from './UserOAuthAccountsTab';
import { UserOAuthTokensTab } from './UserOAuthTokensTab';
import { UserFcmTokensTab } from './UserFcmTokensTab';
import { UserLicencesTab } from './UserLicencesTab';
import { UserEntitlementsTab } from './UserEntitlementsTab';

export const UserShow = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { query } = useShow<UserOut>({ resource: 'iam.users' });
  const { data, isLoading } = query;
  const record = data?.data;

  const { data: canSetRole } = useCan({
    resource: 'iam.users',
    action: 'set_role',
  });

  useRecordDocumentTitle(
    'iam.users',
    record ? (record.email ?? getUserDisplayName(record)) : undefined
  );

  const [activeTab, onTabChange] = useShowTab(USER_SHOW_TABS, USER_DEFAULT_TAB);
  const [grantOpen, setGrantOpen] = useState(false);

  // Entitlements cover the whole account: only offer accounts the user admins.
  const adminAccountIds = useMemo(
    () =>
      Array.from(
        new Set(
          (record?.profiles ?? [])
            .filter((p) => p.role === 'admin')
            .map((p) => p.account_id)
        )
      ),
    [record?.profiles]
  );

  return (
    <Show
      isLoading={isLoading}
      contentProps={bareShowContentProps}
      headerButtons={({ listButtonProps, refreshButtonProps }) => (
        <Space>
          {listButtonProps && <ListButton {...listButtonProps} />}
          {refreshButtonProps && <RefreshButton {...refreshButtonProps} />}
          {record && (
            <UserActionButtons
              userId={record.id}
              isActive={record.is_active}
              isVerified={record.is_verified}
              mode="all"
            />
          )}
          {adminAccountIds.length > 0 && (
            <Button icon={<PlusOutlined />} onClick={() => setGrantOpen(true)}>
              {t('monetization.entitlements.grant.trigger')}
            </Button>
          )}
          {record && <DeleteUserButton record={record} />}
        </Space>
      )}
    >
      {adminAccountIds.length > 0 && (
        <GrantEntitlementModal
          open={grantOpen}
          onClose={() => setGrantOpen(false)}
          initialAccountId={adminAccountIds[0]}
          accountOptions={
            adminAccountIds.length > 1
              ? adminAccountIds.map((id) => ({ value: id, label: id }))
              : undefined
          }
        />
      )}
      {record && (
        <>
          <UserShowHeader
            record={record}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
          <div style={{ marginTop: token.margin }}>
            {activeTab === 'general' && (
              <UserGeneralTab
                record={record}
                canSetRole={canSetRole?.can ?? false}
              />
            )}
            {activeTab === 'consents' && <UserConsentsTab userId={record.id} />}
            {activeTab === 'oauth_accounts' && (
              <UserOAuthAccountsTab userId={record.id} />
            )}
            {activeTab === 'oauth_tokens' && (
              <UserOAuthTokensTab userId={record.id} />
            )}
            {activeTab === 'fcm_tokens' && (
              <UserFcmTokensTab userId={record.id} />
            )}
            {activeTab === 'licences' && <UserLicencesTab record={record} />}
            {activeTab === 'entitlements' && (
              <UserEntitlementsTab record={record} />
            )}
          </div>
        </>
      )}
    </Show>
  );
};
